"use client";

import styled from "@emotion/styled";
import type { ButtonProps as AntButtonProps } from "antd";
import { Button } from "antd";
import type { ReactNode } from "react";
import React from "react";
import { OCTOLANE_COLOR_TOKENS } from "~/app/styles/tokens";

import If from "~/core/ui/If";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";

import ShortcutIcons from "./ShortcutIcons";

interface TextButtonProps extends AntButtonProps {
  disableHover?: boolean;
  shortcutIcons?: React.ReactNode | IconProp[];
  icon?: React.ReactNode;
}

const StyledTextButton = styled(Button, {
  shouldForwardProp: prop =>
    !["shortcutIcons", "hasIcon", "disableHover"].includes(prop),
})<{
  shortcutIcons: React.ReactNode | IconProp[];
  hasIcon: boolean;
  disableHover?: boolean;
}>`
  &.ant-btn-text {
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-weight: 500;
    color: ${OCTOLANE_COLOR_TOKENS.text.light};
    gap: 8px;
    &:focus-visible {
      outline: 2px solid ${OCTOLANE_COLOR_TOKENS.border.base} !important;
    }
    &:focus {
      outline: none;
    }
    &:hover {
      ${({ disableHover }) =>
        !disableHover
          ? `background: ${OCTOLANE_COLOR_TOKENS.bg.lightest} !important;`
          : `background: transparent !important;`}
      color: ${OCTOLANE_COLOR_TOKENS.text.light} !important;
    }
    & > .ant-btn-icon {
      margin-inline-end: 0px !important;
    }
  }
  &.ant-btn-lg {
    height: 32px;
    padding: ${({ shortcutIcons, hasIcon }) =>
      shortcutIcons || hasIcon ? "4px 8px" : "4px 16px"};
    font-size: 14px;
  }
  &.ant-btn-sm {
    height: 24px;
    padding: 4px 8px;
    font-size: 12px !important;
  }
`;

const TextButton: React.FC<TextButtonProps> = ({
  shortcutIcons,
  iconPosition = "start",
  size = "large",
  icon,
  type = "text",
  disableHover = false,
  children,
  ...rest
}) => {
  return (
    <StyledTextButton
      shortcutIcons={shortcutIcons}
      hasIcon={!!icon}
      type={type}
      size={size}
      icon={icon}
      iconPosition={iconPosition}
      disableHover={disableHover}
      {...rest}
    >
      {children}
      <If condition={Array.isArray(shortcutIcons)}>
        <ShortcutIcons icons={shortcutIcons as IconProp[]} />
      </If>
      <If condition={!Array.isArray(shortcutIcons)}>
        {shortcutIcons as ReactNode}
      </If>
    </StyledTextButton>
  );
};

export default TextButton;
